import { useMemo } from 'react';
import type { IFieldsTab } from '@moko/utils';
import { useOtherInfoContext } from '../OtherInfoContext';
import { FormArea } from '../FormArea';
import { FieldList } from '../FieldList';
import {
  MobileOtherInfoProvider,
  useMobileOtherInfo,
} from './MobileOtherInfoContext';
import { SectionHeader } from './SectionHeader';
import { BottomNavBar } from './BottomNavBar';
import { NavigationView } from './NavigationView';
import { MobileTabContent } from './MobileTabContent';
const contentStyle = {
  width: '100%' as const,
  minWidth: 0,
  boxSizing: 'border-box' as const,
};

const scrollStyle = {
  ...contentStyle,
  flex: 1,
  minHeight: 0,
  overflowY: 'auto' as const,
};

const shellStyle = (contained: boolean) =>
  contained
    ? {
        display: 'flex' as const,
        flexDirection: 'column' as const,
        height: '100%',
        minHeight: 0,
        minWidth: 0,
        flex: 1,
        overflow: 'hidden',
        width: '100%',
      }
    : { paddingBottom: 60, width: '100%' };

const MobileOtherInfoInner = () => {
  const { params } = useOtherInfoContext();
  const { showNavigation, tabs } = useMobileOtherInfo();
  const hasTabs = tabs.length > 0;
  const contained = Boolean(params.contained);

  if (tabs.length === 1) {
    return (
      <FormArea style={contained ? scrollStyle : contentStyle}>
        <SectionHeader />
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
            marginTop: 12,
            width: '100%',
            minWidth: 0,
            boxSizing: 'border-box',
          }}
        >
          <MobileTabContent />
        </div>
      </FormArea>
    );
  }

  if (!hasTabs) {
    return (
      <FormArea style={contained ? scrollStyle : contentStyle}>
        <FieldList fields={params.metadata.fields} parentName="root" />
      </FormArea>
    );
  }

  if (showNavigation) {
    return (
      <div style={shellStyle(contained)}>
        <div style={contained ? scrollStyle : contentStyle}>
          <NavigationView fields={params.metadata.fields} />
        </div>
        <BottomNavBar />
      </div>
    );
  }

  return (
    <div style={shellStyle(contained)}>
      <FormArea style={contained ? scrollStyle : contentStyle}>
        <SectionHeader />
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
            marginTop: 12,
            width: '100%',
            minWidth: 0,
            boxSizing: 'border-box',
          }}
        >
          <MobileTabContent />
        </div>
      </FormArea>
      <BottomNavBar />
    </div>
  );
};

export const MobileOtherInfo = () => {
  const { params, effectsKey } = useOtherInfoContext();
  const tabs: IFieldsTab[] = useMemo(
    () => (params.metadata.tabs ?? []).filter((tab) => !tab.hidden),
    [params.metadata.tabs, effectsKey]
  );

  return (
    <MobileOtherInfoProvider tabs={tabs} fields={params.metadata.fields}>
      <div
        style={
          params.contained
            ? {
                height: '100%',
                minHeight: 0,
                minWidth: 0,
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              }
            : undefined
        }
      >
        <MobileOtherInfoInner />
      </div>
    </MobileOtherInfoProvider>
  );
};
